import { Ng1StateDeclaration } from '@uirouter/angularjs/lib/interface';

/**
 * @ngdoc type
 * @name RoutingItem
 * @module mx.app
 *
 * @property {string} name - Gets or sets Unique name of the Routing item
 * @property {string} viewUrl - Gets or sets Routing item view url
 * @property {string} path - Gets or sets path for the Routing item. '/' + routing item name is used by default.
 * @property {string} title - Gets or sets title for the Routing item. '/' + routing item name is used by default
 * @property {string} controller - Gets or sets controller name for the Routing item. Routing item name + 'Controller' is used by default
 * @property {boolean} unauthorized - Possibility to open a route without authorization
 * @property {Object} resolve - Gets or sets object with resolve options
 * @property {string} template - template for the route
 * @property {angular.component} component - component to render with this route. Uses instead of controller and template/viewUrl
 * @property {Object} params - object with default values of parameters in the path
 * @property {function|string} redirectTo - either name of state or function with input parameter '$transition$' of ui-router to make optional redirect
 *
 * @description
 * Routing item class.
 * Constructor accepts name, viewUrl, path, title, ctrlName, as params and passes values to properties like:
 * ```javascript
 var routingItem = new mx.shell.RoutingItem(name, viewUrl, path, title, ctrlName);
 * ```
 */
export type NamedStateDeclaration = Ng1StateDeclaration & { name: string };

export type RoutingItemOptions = {
	name?: NamedStateDeclaration['name'];
	viewUrl?: NamedStateDeclaration['templateUrl'];
	path?: NamedStateDeclaration['url'];
	controller?: string;
	resolve?: NamedStateDeclaration['resolve'];
	template?: NamedStateDeclaration['template'];
	component?: NamedStateDeclaration['component'];
	params?: NamedStateDeclaration['params'];
	redirectTo?: NamedStateDeclaration['redirectTo'];
	onEnter?: NamedStateDeclaration['onEnter'];
	unauthorized?: boolean;
};

export class RoutingItem {
	name: NamedStateDeclaration['name'];
	viewUrl: NamedStateDeclaration['templateUrl'];
	path: NamedStateDeclaration['url'];
	controllerName?: string;
	resolve: NamedStateDeclaration['resolve'];
	template: NamedStateDeclaration['template'];
	component: NamedStateDeclaration['component'];
	params: NamedStateDeclaration['params'];
	redirectTo: NamedStateDeclaration['redirectTo'];
	onEnter: NamedStateDeclaration['onEnter'];
	unauthorized = false;

	constructor(options: RoutingItemOptions) {
		this.name = options.name ?? '';
		this.resolve = options.resolve;
		this.params = options.params;
		this.redirectTo = options.redirectTo;
		this.onEnter = options.onEnter;
		this.unauthorized = Boolean(options.unauthorized);

		if (options.path) {
			this.path = options.path.startsWith('/') ? options.path : `/${options.path}`;
		} else {
			this.path = `/${this.name}`;
		}

		if (options.component) {
			this.component = options.component;
		} else {
			let controllerName = options.controller;

			if (!controllerName) {
				controllerName = `${this.name}Controller`;
				controllerName = controllerName.charAt(0).toUpperCase() + controllerName.slice(1);
			}

			// If controller doesn't contain 'controllerAs' alias then it'll be 'vm'
			if (controllerName.indexOf(' as ') === -1) {
				controllerName += ' as vm';
			}
			this.controllerName = controllerName;
			this.viewUrl = options.viewUrl;
			this.template = options.template;
		}
	}

	toState() {
		const state: NamedStateDeclaration = {
			name: this.name,
			url: this.path,
			params: this.params,
			resolve: this.resolve,
			redirectTo: this.redirectTo,
			onEnter: this.onEnter,
		};

		if (this.component) {
			state.component = this.component;
		} else {
			Object.assign(state, {
				templateUrl: this.viewUrl,
				template: this.template,
				controller: this.controllerName,
				controllerAs: this.name,
			});
		}

		return state;
	}
}
